import React, { useState, useEffect } from 'react';
import { X, Minus, Plus } from 'lucide-react';
import { useGuestContext } from '../../contexts/GuestContext';

const GuestSelectorModal = ({ onClose, onSelect, initialGuests = null }) => {
  const { guestData: globalGuests, updateGuests: updateGlobalGuests } = useGuestContext();
  
  // Use global guests if initialGuests not provided, otherwise use initialGuests
  const effectiveInitialGuests = initialGuests || globalGuests;
  
  const [guestData, setGuestData] = useState(effectiveInitialGuests);
  
  // Sync with global guests when modal opens (if no initialGuests provided)
  useEffect(() => {
    if (!initialGuests) {
      setGuestData(globalGuests);
    }
  }, [initialGuests, globalGuests]);

  // Prevent background scrolling when modal is open
  useEffect(() => {
    // Store original body styles
    const originalStyle = window.getComputedStyle(document.body);
    const originalOverflow = originalStyle.overflow;
    const originalPosition = originalStyle.position;
    const originalTop = originalStyle.top;
    
    // Get current scroll position
    const scrollY = window.scrollY;
    
    // Apply scroll prevention
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    
    // Add CSS class for additional scroll prevention
    document.body.classList.add('modal-open');
    document.documentElement.classList.add('modal-open');
    
    // Cleanup: restore scrolling when modal closes
    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.position = originalPosition;
      document.body.style.top = originalTop;
      document.body.style.width = '';
      document.body.classList.remove('modal-open');
      document.documentElement.classList.remove('modal-open');
      
      // Restore scroll position
      window.scrollTo(0, scrollY);
    };
  }, []);

  const updateCount = (field, increment) => {
    setGuestData(prev => ({
      ...prev,
      [field]: Math.max(0, prev[field] + increment)
    }));
  };

  const formatGuestString = () => {
    const { rooms, adults, children, pets } = guestData;
    return {
      roomText: `${rooms} Room${rooms > 1 ? 's' : ''}`,
      guestText: `${adults} Adult${adults > 1 ? 's' : ''} ${children} Child${children !== 1 ? 'ren' : ''} ${pets} Pet${pets > 1 ? 's' : ''}`
    };
  };

  const handleProceed = () => {
    // Update global guest context
    updateGlobalGuests(guestData);
    
    // Format guest string
    const guestInfo = formatGuestString();
    const fullString = `${guestInfo.roomText} ${guestInfo.guestText}`;
    
    // Call the onSelect callback if provided
    if (onSelect) {
      onSelect(fullString);
    }
    
    onClose();
  };

  const CounterRow = ({ label, sublabel, field, minimum = 0 }) => (
    <div className="flex items-center justify-between p-[16px] border-b border-[#F0F2F5] last:border-b-0">
      <div className='flex flex-col gap-[12px]'>
        <div className="text-[18px] font-medium text-black leading-none">{label}</div>
        {sublabel && <div className="text-[16px] text-[#b0b0b0] leading-none">{sublabel}</div>}
      </div>
      <div className="flex items-center border border-[1px] border-[#ddd] p-[6px] rounded-[8px] space-x-[14px]">
        <button
          onClick={() => updateCount(field, -1)}
          disabled={guestData[field] <= minimum}
          className="flex items-center justify-center hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="19" viewBox="0 0 18 19" fill="none">
            <g clip-path="url(#clip0_431_4349)">
              <path d="M3.75031 9.5H14.2503" stroke="black" stroke-width="1.35" stroke-linecap="round" stroke-linejoin="round"/>
            </g>
            <defs>
              <clipPath id="clip0_431_4349">
                <rect width="18" height="18" fill="white" transform="translate(0 0.5)"/>
              </clipPath>
            </defs>
          </svg>
        </button>
        <span className=" text-center text-[16px] w-[16px] font-semibold leading-none text-[#6b6b6b]">{guestData[field]}</span>
        <button
          onClick={() => updateCount(field, 1)}
          className="flex items-center justify-center hover:bg-gray-50 transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="19" viewBox="0 0 18 19" fill="none">
            <g clip-path="url(#clip0_431_4353)">
              <path d="M9 4.25V14.75" stroke="black" stroke-width="1.35" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M3.75031 9.5H14.2503" stroke="black" stroke-width="1.35" stroke-linecap="round" stroke-linejoin="round"/>
            </g>
            <defs>
              <clipPath id="clip0_431_4353">
                <rect width="18" height="18" fill="white" transform="translate(0 0.5)"/>
              </clipPath>
            </defs>
          </svg>
        </button>
      </div>
    </div>
  );

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end md:items-center justify-center"
      onTouchMove={(e) => e.preventDefault()}
      onWheel={(e) => e.preventDefault()}
    >
      {/* Slide-up Modal - Sticky to Bottom */}
      <div className="bg-white rounded-t-[32px] md:rounded-[32px] w-full md:w-[900px] max-h-[70vh] overflow-y-auto animate-slide-up">
        
        {/* Header */}
        <div className='sticky top-0 px-[16px] pt-[20px] border-b-[1px] border-[#dddddd86] bg-white z-10'>
          <div className="flex items-center justify-between mb-[20px]">
            <h2 className="text-[20px] font-medium text-black tracking-[-1px]">Select Rooms and Guest in date</h2>
            <button 
              onClick={onClose}
              className=" hover:bg-gray-100 rounded-full transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28" fill="none">
                <g clip-path="url(#clip0_2904_2203)">
                  <path d="M21 7L7 21" stroke="#4B4B4B" stroke-width="2.33333" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M7 7L21 21" stroke="#4B4B4B" stroke-width="2.33333" stroke-linecap="round" stroke-linejoin="round"/>
                </g>
                <defs>
                  <clipPath id="clip0_2904_2203">
                    <rect width="28" height="28" fill="white"/>
                  </clipPath>
                </defs>
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-[16px] flex flex-col gap-[10px]">
          <CounterRow 
            label="Rooms" 
            sublabel="Minimum 1" 
            field="rooms" 
            minimum={1} 
          />
          <CounterRow 
            label="Adults" 
            sublabel="13 years & above" 
            field="adults" 
            minimum={1} 
          />
          <CounterRow 
            label="Children" 
            sublabel="0-12 years" 
            field="children" 
          />
          <CounterRow 
            label="Pets" 
            field="pets" 
          />
        </div>

        {/* Bottom Summary and Action - Fixed to Bottom */}
        <div className="sticky bottom-0 bg-white border-t border-[#dddddd86] p-[16px]">
          <div className="flex items-center justify-between">
            {/* Selected Guest Summary */}
            <div className="flex flex-col">
              <div className="text-[16px] font-normal text-black">
                {formatGuestString().roomText}
              </div>
              <div className="text-[14px] text-[#b0b0b0]">
                {formatGuestString().guestText}
              </div>
            </div>
            
            {/* Proceed Button */}
            <button
              onClick={handleProceed}
              className="bg-black text-white py-[12px] text-[18px] px-[50px] rounded-[12px] font-semibold hover:bg-gray-800 transition-colors"
            >
              Proceed
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuestSelectorModal; 